import { useEffect } from 'react';

import { LANDING_INDEXABLE } from '@/lib/indexacionGate';
import { bloquesDe } from '@/lib/jsonLd';
import { IDIOMA_RAIZ, alternativasDe, urlDe } from '@/lib/rutasPublicas';
import { sueltaDe } from '@/lib/rutasSueltas';
import { OG_ALTO, OG_ANCHO, seoDe } from '@/pages/landingV3.seo';

/**
 * El `<head>` de una página pública: título, descripción, canonical, hreflang,
 * `<html lang>`, Open Graph y Twitter.
 *
 * ── QUÉ HABÍA ANTES ─────────────────────────────────────────────────────────
 *
 * `<title>Mytapstar</title>` para las treinta y pico rutas públicas, cero
 * `<meta name="description">`, cero `canonical`, cero `hreflang` y un `<html>`
 * sin `lang`. En una lista de resultados el título es la mitad de la decisión
 * de entrar; con el mismo para todo, cada página competía consigo misma.
 *
 * ── DOS CONSUMIDORES, UNA SOLA FUENTE ───────────────────────────────────────
 *
 * `etiquetasSeo()` devuelve la lista de etiquetas como DATOS. La usan dos:
 *
 *   - `useSeoHead`, que las mete en el DOM del navegador.
 *   - `scripts/prerender.mjs`, que las escribe en el HTML del build.
 *
 * Tiene que ser una sola función porque si cada uno construyera su `<head>`,
 * el estático y el del cliente se separarían — y el que ve Google es el
 * estático, o sea el que nadie mira al desarrollar.
 *
 * ── POR QUÉ NO SE AÑADE `react-helmet` ──────────────────────────────────────
 *
 * Porque con el prerender el `<head>` lo escribe el build, y una dependencia de
 * gestión de `<head>` en el cliente sobraría. Esto generaliza el `useNoIndex`
 * que ya existía en `LandingShellV3`.
 *
 * ── LO IMPORTANTE DE LOS HREFLANG ───────────────────────────────────────────
 *
 * Tienen que ser RECÍPROCOS y AUTORREFERENTES: cada página declara las cinco
 * versiones incluida la suya. Un grupo en el que A apunte a B pero B no apunte
 * a A, Google lo ignora entero — no avisa, simplemente no lo usa. Como salen de
 * `alternativasDe()`, que lee la misma tabla que el router, no pueden
 * descuadrarse.
 *
 * El `x-default` va al español, que es el idioma de la raíz: es lo que Google
 * sirve a quien no encaja en ninguno de los cinco.
 */

/** `es` → `es_ES`. Open Graph quiere el locale completo, no el idioma suelto. */
const LOCALES_OG = {
  es: 'es_ES',
  en: 'en_GB',
  pt: 'pt_PT',
  de: 'de_DE',
  fr: 'fr_FR',
};

const meta = (name, content) => ({
  tag: 'meta', attrs: { name, content }, selector: `meta[name="${name}"]`,
});
const propiedad = (property, content) => ({
  tag: 'meta', attrs: { property, content }, selector: `meta[property="${property}"]`,
});

/**
 * Las etiquetas del `<head>` de una página, como datos.
 *
 * @param {string} pagina  clave de `PAGINAS` en `lib/rutasPublicas.js`
 * @param {string} idioma
 * @param {string} base    el origen para las URLs absolutas (`https://…`)
 * @returns {{titulo: string|null, lang: string, etiquetas: Array}}
 */
export function etiquetasSeo(pagina, idioma, base) {
  const etiquetas = [];

  /*
   * El `noindex` va PRIMERO, antes de cualquier salida anticipada.
   *
   * Si una página no estuviera en la tabla de SEO saldríamos por el `return` de
   * abajo, y sin esto se quedaría sin `noindex` justamente en staging — donde
   * el riesgo es que Google indexe una copia entera del sitio. Es la etiqueta
   * que no puede depender de que el resto haya ido bien.
   */
  if (!LANDING_INDEXABLE) etiquetas.push(meta('robots', 'noindex, nofollow'));

  const seo = seoDe(pagina, idioma);
  const url = urlDe(pagina, idioma);
  if (!seo || !url) return { titulo: null, lang: idioma, etiquetas };

  const canonica = `${base}${url}`;
  const imagen = `${base}${seo.imagen}`;

  etiquetas.push(meta('description', seo.descripcion));
  etiquetas.push({
    tag: 'link',
    attrs: { rel: 'canonical', href: canonica },
    selector: 'link[rel="canonical"]',
  });

  for (const alt of alternativasDe(pagina)) {
    etiquetas.push({
      tag: 'link',
      attrs: { rel: 'alternate', hreflang: alt.idioma, href: `${base}${alt.url}` },
      selector: `link[rel="alternate"][hreflang="${alt.idioma}"]`,
    });
  }
  etiquetas.push({
    tag: 'link',
    attrs: { rel: 'alternate', hreflang: 'x-default', href: `${base}${urlDe(pagina, IDIOMA_RAIZ)}` },
    selector: 'link[rel="alternate"][hreflang="x-default"]',
  });

  /*
   * Open Graph. La imagen va absoluta y con `width`/`height` declarados: sin
   * las medidas, la plataforma tiene que descargarla antes de decidir el hueco
   * y muchas veces enseña el enlace sin ella la primera vez que se comparte.
   * `og:image:alt` es lo que lee quien no ve la miniatura.
   */
  etiquetas.push(propiedad('og:title', seo.titulo));
  etiquetas.push(propiedad('og:description', seo.descripcion));
  etiquetas.push(propiedad('og:url', canonica));
  etiquetas.push(propiedad('og:type', 'website'));
  etiquetas.push(propiedad('og:site_name', 'MyTapstar'));
  etiquetas.push(propiedad('og:locale', LOCALES_OG[idioma] || LOCALES_OG.es));
  etiquetas.push(propiedad('og:image', imagen));
  etiquetas.push(propiedad('og:image:width', String(OG_ANCHO)));
  etiquetas.push(propiedad('og:image:height', String(OG_ALTO)));
  etiquetas.push(propiedad('og:image:alt', seo.titulo));

  // `summary_large_image` es lo que hace que la miniatura salga grande en vez
  // de como un cuadradito al lado del texto.
  etiquetas.push(meta('twitter:card', 'summary_large_image'));
  etiquetas.push(meta('twitter:title', seo.titulo));
  etiquetas.push(meta('twitter:description', seo.descripcion));
  etiquetas.push(meta('twitter:image', imagen));

  /*
   * Los datos estructurados. La home declara la empresa, el sitio y la
   * aplicación; las interiores su miga de pan. El FAQ NO sale de aquí: lo pinta
   * `FaqAcordeon` junto a las preguntas que describe, por el motivo que explica
   * la cabecera de `lib/jsonLd.js`.
   *
   * Son los únicos descriptores con `contenido`, y por eso los dos
   * serializadores —`ponEtiqueta()` aquí abajo y `serializa()` en
   * `scripts/prerender.mjs`— han tenido que aprender a emitir una etiqueta con
   * hijos. Hasta este punto todas eran vacías.
   */
  etiquetas.push(...bloquesDe(pagina, idioma, base));

  return { titulo: seo.titulo, lang: idioma, etiquetas };
}

/**
 * El `<head>` de una página SUELTA: las cinco legales y, desde el PR siguiente,
 * la de 404.
 *
 * ── EN QUÉ SE DIFERENCIA DE `etiquetasSeo` ──────────────────────────────────
 *
 * En dos cosas, y las dos se siguen de que estas páginas tienen UNA sola URL
 * para los cinco idiomas:
 *
 *  - **No llevan `hreflang`.** No hay cinco versiones entre las que Google
 *    pueda elegir, así que declarar el grupo sería mentir. Un `hreflang` que
 *    apunta cinco veces a la misma URL no es «neutro»: es un grupo mal formado,
 *    y Google descarta el grupo entero sin decir nada.
 *  - **No llevan datos estructurados.** La empresa ya la declara la home; un
 *    `Organization` repetido en cinco páginas legales no añade nada y multiplica
 *    los sitios donde puede quedarse desactualizado.
 *
 * Lo que sí llevan es lo que faltaba: título propio, descripción, canonical
 * autorreferente y Open Graph, para que un `/privacy` compartido por WhatsApp
 * deje de salir sin nada.
 *
 * @param {string} clave  clave de `SUELTAS` en `lib/rutasSueltas.js`
 * @param {string} base   el origen para las URLs absolutas
 */
export function etiquetasSeoSueltas(clave, base) {
  const etiquetas = [];
  const ficha = sueltaDe(clave);

  // El `noindex` primero y antes de cualquier salida anticipada, por lo mismo
  // que en `etiquetasSeo`: es la etiqueta que no puede depender de que el resto
  // haya ido bien. Aquí hay dos motivos para ponerlo — el entorno, o que la
  // propia página pida no indexarse (el 404).
  if (!LANDING_INDEXABLE || ficha?.indexable === false) {
    etiquetas.push(meta('robots', 'noindex, nofollow'));
  }
  if (!ficha) return { titulo: null, lang: IDIOMA_RAIZ, etiquetas };

  /*
   * Una página no indexable —hoy solo la de error— se queda con el título y el
   * `noindex`, y nada más.
   *
   * El canonical es el que NO puede estar: la de error se sirve desde cualquier
   * url que no exista, así que un `<link rel="canonical">` le diría a Google que
   * esa url inventada es la versión buena de algo. Y la descripción y el Open
   * Graph sobran: no hay nada que resumir y nadie comparte un 404.
   */
  if (ficha.indexable === false) {
    return { titulo: ficha.titulo, lang: IDIOMA_RAIZ, etiquetas };
  }

  const canonica = `${base}${ficha.url}`;
  const imagen = `${base}${ficha.imagen}`;

  etiquetas.push(meta('description', ficha.descripcion));
  etiquetas.push({
    tag: 'link',
    attrs: { rel: 'canonical', href: canonica },
    selector: 'link[rel="canonical"]',
  });

  etiquetas.push(propiedad('og:title', ficha.titulo));
  etiquetas.push(propiedad('og:description', ficha.descripcion));
  etiquetas.push(propiedad('og:url', canonica));
  etiquetas.push(propiedad('og:type', 'website'));
  etiquetas.push(propiedad('og:site_name', 'MyTapstar'));
  etiquetas.push(propiedad('og:locale', LOCALES_OG[IDIOMA_RAIZ]));
  etiquetas.push(propiedad('og:image', imagen));
  etiquetas.push(propiedad('og:image:width', String(OG_ANCHO)));
  etiquetas.push(propiedad('og:image:height', String(OG_ALTO)));
  etiquetas.push(propiedad('og:image:alt', ficha.titulo));

  etiquetas.push(meta('twitter:card', 'summary_large_image'));
  etiquetas.push(meta('twitter:title', ficha.titulo));
  etiquetas.push(meta('twitter:description', ficha.descripcion));
  etiquetas.push(meta('twitter:image', imagen));

  return { titulo: ficha.titulo, lang: IDIOMA_RAIZ, etiquetas };
}

/**
 * De dónde sale el dominio de las URLs absolutas en el navegador.
 *
 * `canonical` y `og:url` TIENEN que ser absolutas — una relativa la ignoran
 * tanto Google como las plataformas sociales. Se toma de `window.location` en
 * vez de fijar `https://mytapstar.com` para que staging se declare a sí mismo:
 * si apuntara a producción, le estaríamos diciendo a Google que la versión
 * buena de la página de staging es la de producción, y esta acabaría recibiendo
 * señales de un dominio que ni debería rastrearse.
 */
function origen() {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

/**
 * Crea o actualiza una etiqueta y devuelve el elemento.
 *
 * Reutiliza la que ya exista con el mismo selector en vez de añadir otra: sin
 * esto, navegar entre cinco páginas del SPA deja cinco canonical, y el que vale
 * es el primero.
 *
 * `contenido` solo lo traen los bloques de datos estructurados. Va por
 * `textContent` y no por `innerHTML` justamente porque el navegador no debe
 * volver a interpretarlo: el JSON ya sale de `aJson()` con los `<` escapados a
 * `<`, y dejarlo en texto plano es lo que garantiza que ni el navegador ni
 * nadie lo reinterprete como marcado.
 */
function ponEtiqueta({ tag, attrs, selector, contenido }) {
  const previa = document.head.querySelector(selector);
  const el = previa || document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  if (contenido !== undefined) el.textContent = contenido;
  if (!previa) document.head.appendChild(el);
  return el;
}

/**
 * @param {string} pagina  clave de `PAGINAS`
 * @param {string} idioma  el idioma que se está sirviendo
 */
export default function useSeoHead(pagina, idioma) {
  useEffect(() => {
    const { titulo, lang, etiquetas } = etiquetasSeo(pagina, idioma, origen());

    const tituloPrevio = document.title;
    const langPrevio = document.documentElement.getAttribute('lang');
    const puestas = etiquetas.map(ponEtiqueta);

    if (titulo) document.title = titulo;
    // Lo lee el lector de pantalla para elegir la voz, y el CMP de cookies
    // (`index.html` lo suple a mano justamente porque faltaba).
    document.documentElement.setAttribute('lang', lang);

    return () => {
      for (const el of puestas) el.remove();
      document.title = tituloPrevio;
      if (langPrevio) document.documentElement.setAttribute('lang', langPrevio);
      else document.documentElement.removeAttribute('lang');
    };
  }, [pagina, idioma]);
}
